import React, { useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DatePickerPopover, Dialog, FCWithFragments } from '@modernloop/shared/components';
import { IsoTimestamp, assertIsoTimestamp, formatTZ, getLocalTimezone } from '@modernloop/shared/datetime';
import { CrossIcon, DateIcon } from '@modernloop/shared/icons';
import { IconButton, Stack, TextField, Typography } from '@mui/material';

import { EditKeyDatesModal_ModuleMemberFragment, useModuleMemberUpdateMutation } from 'src/generated/mloop-graphql';

type Fragments = {
  moduleMember: EditKeyDatesModal_ModuleMemberFragment;
};

type Props = {
  onClose: () => void;
};

const EditKeyDatesModal: FCWithFragments<Fragments, Props> = ({ moduleMember, onClose }) => {
  const { t } = useTranslation();
  const timezone = getLocalTimezone();
  const [showAddedToModuleDatePicker, setShowAddedToModuleDatePicker] = useState(false);
  const [showFullyTrainedDatePicker, setShowFullyTrainedDatePicker] = useState(false);

  const [addedToModuleDate, setAddedToModuleDate] = useState<IsoTimestamp | undefined>(moduleMember.createdAt);
  const [fullyTrainedDate, setFullyTrainedDate] = useState<IsoTimestamp | undefined>(moduleMember.trainedAt);

  const addedToModuleDateAnchorEl = useRef<HTMLInputElement>(null);
  const fullyTrainedDateAnchorEl = useRef<HTMLInputElement>(null);

  const [editKeyDatesMutation, { loading }] = useModuleMemberUpdateMutation();

  const handleSave = async () => {
    try {
      await editKeyDatesMutation({
        variables: {
          input: {
            interviewModuleId: moduleMember.interviewModuleId,
            employeeId: moduleMember.employeeId,
            trainedAt: fullyTrainedDate,
            addedToModuleAt: addedToModuleDate,
            clearTrainedAt: !fullyTrainedDate,
          },
        },
      });

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog
      open
      maxWidth="xs"
      title={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.editKeyDates.title')}
      decoration={
        <img
          src="/static/images/setup/decoration_edit.svg"
          alt={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.editTrainingPlan')}
          height="64px"
          width="64px"
        />
      }
      onClose={onClose}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      submitOptions={{
        label: t('hiring-dashboard-common:common.save'),
        onClick: handleSave,
        isLoading: loading,
        isDisabled: loading,
      }}
    >
      <Stack gap={2.5}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography>
              {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.addedToModule')}
            </Typography>
            <TextField
              inputRef={addedToModuleDateAnchorEl}
              variant="outlined"
              value={addedToModuleDate ? formatTZ(addedToModuleDate, timezone, 'MMM d, yyyy') : ''}
              inputProps={{ onClick: () => setShowAddedToModuleDatePicker(true), readOnly: true }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                startAdornment: <DateIcon />,
              }}
              sx={{ width: '50%' }}
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography>
              {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.fullyTrained')}
            </Typography>
            <TextField
              inputRef={fullyTrainedDateAnchorEl}
              variant="outlined"
              value={fullyTrainedDate ? formatTZ(fullyTrainedDate, timezone, 'MMM d, yyyy') : ''}
              inputProps={{ onClick: () => setShowFullyTrainedDatePicker(true), readOnly: true }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                startAdornment: <DateIcon />,
                endAdornment: (
                  <IconButton
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFullyTrainedDate(undefined);
                    }}
                  >
                    <CrossIcon />
                  </IconButton>
                ),
              }}
              sx={{ width: '50%' }}
            />
          </Stack>
        </Stack>

        <Typography color="text.secondary">
          {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.editKeyDates.additionalKeyDatesNote')}
        </Typography>
      </Stack>
      {showAddedToModuleDatePicker && (
        <DatePickerPopover
          open
          disableFuture
          disablePast={false}
          timezone={timezone}
          anchorEl={addedToModuleDateAnchorEl.current}
          date={addedToModuleDate || assertIsoTimestamp(new Date().toISOString())}
          onChange={(date) => {
            setAddedToModuleDate(date);
            setShowAddedToModuleDatePicker(false);
          }}
          onClose={() => setShowAddedToModuleDatePicker(false)}
        />
      )}
      {showFullyTrainedDatePicker && (
        <DatePickerPopover
          open
          disableFuture
          disablePast={false}
          timezone={timezone}
          anchorEl={fullyTrainedDateAnchorEl.current}
          date={fullyTrainedDate || assertIsoTimestamp(new Date().toISOString())}
          onChange={(date) => {
            setFullyTrainedDate(date);
            setShowFullyTrainedDatePicker(false);
          }}
          onClose={() => setShowFullyTrainedDatePicker(false)}
        />
      )}
    </Dialog>
  );
};

EditKeyDatesModal.fragments = {
  moduleMember: gql`
    fragment EditKeyDatesModal_moduleMember on InterviewModuleMember {
      id
      interviewModuleId
      employeeId
      trainedAt
      createdAt
    }
  `,
};

export default EditKeyDatesModal;
